import React, { useState, useEffect } from 'react';
import { Building2, MapPin, Phone, Mail, Globe, Users, Bed, Star } from 'lucide-react';
import { getHotel } from '../../services/managerAPI';
import './HotelOverview.css';

const HotelOverview = () => {
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchHotelDetails();
  }, []);

  const fetchHotelDetails = async () => {
    try {
      setLoading(true);
      const response = await getHotel();
      
      if (response.success) {
        setHotel(response.data);
      }
    } catch (err) {
      console.error('Error fetching hotel details:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load hotel details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="hotel-overview-loading">
        <div className="spinner"></div>
        <p>Loading hotel details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hotel-overview-error">
        <p>{error}</p>
        <button onClick={fetchHotelDetails} className="retry-btn">Retry</button>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="hotel-overview-empty">
        <Building2 size={64} />
        <h3>No Hotel Found</h3>
        <p>Please complete the hotel registration process to view details.</p>
      </div>
    );
  }

  return (
    <div className="hotel-overview">
      <div className="hotel-header-card">
        <div className="hotel-main-info">
          <div className="hotel-icon">
            <Building2 size={48} />
          </div>
          <div className="hotel-title-section">
            <h1>{hotel.name}</h1>
            <div className="hotel-category">{hotel.category}</div>
            {hotel.status && (
              <span className={`status-badge ${hotel.status}`}>
                {hotel.status.replace('_', ' ').toUpperCase()}
              </span>
            )}
          </div>
        </div>

        {hotel.description && (
          <p className="hotel-description">{hotel.description}</p>
        )}
      </div>

      <div className="hotel-details-grid">
        {/* Contact Information */}
        <div className="detail-card">
          <h3>Contact Information</h3>
          <div className="detail-items">
            {hotel.phone && (
              <div className="detail-item">
                <Phone size={18} />
                <span>{hotel.phone}</span>
              </div>
            )}
            {hotel.email && (
              <div className="detail-item">
                <Mail size={18} />
                <span>{hotel.email}</span>
              </div>
            )}
            {hotel.website && (
              <div className="detail-item">
                <Globe size={18} />
                <a href={hotel.website} target="_blank" rel="noopener noreferrer">
                  {hotel.website}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Location */}
        <div className="detail-card">
          <h3>Location</h3>
          <div className="detail-items">
            <div className="detail-item">
              <MapPin size={18} />
              <div className="address-details">
                <div>{hotel.address}</div>
                <div>{hotel.city}, {hotel.state} {hotel.postal_code}</div>
                <div>{hotel.country}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Property Details */}
        <div className="detail-card">
          <h3>Property Details</h3>
          <div className="detail-items">
            {hotel.total_floors && (
              <div className="detail-item">
                <Building2 size={18} />
                <span>{hotel.total_floors} Floors</span>
              </div>
            )}
            {hotel.total_rooms && (
              <div className="detail-item">
                <Bed size={18} />
                <span>{hotel.total_rooms} Total Rooms</span>
              </div>
            )}
            {hotel.check_in_time && (
              <div className="detail-item">
                <span className="label">Check-in:</span>
                <span>{hotel.check_in_time}</span>
              </div>
            )}
            {hotel.check_out_time && (
              <div className="detail-item">
                <span className="label">Check-out:</span>
                <span>{hotel.check_out_time}</span>
              </div>
            )}
          </div>
        </div>

        {/* Staff Information */}
        {hotel.staff_count !== undefined && (
          <div className="detail-card">
            <h3>Staff</h3>
            <div className="detail-items">
              <div className="detail-item">
                <Users size={18} />
                <span>{hotel.staff_count} Staff Members</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Configuration Status */}
      <div className="configuration-status">
        <h3>Configuration Status</h3>
        <div className="status-checklist">
          <div className={`checklist-item ${hotel.status !== 'pending' && hotel.status !== 'configuring' ? 'completed' : ''}`}>
            <span className="check-icon">{hotel.status !== 'pending' && hotel.status !== 'configuring' ? '✓' : '○'}</span>
            <span>Hotel Details Complete</span>
          </div>
          <div className="checklist-item">
            <span className="check-icon">○</span>
            <span>Facilities Configured</span>
          </div>
          <div className="checklist-item">
            <span className="check-icon">○</span>
            <span>Room Types Added</span>
          </div>
          <div className="checklist-item">
            <span className="check-icon">○</span>
            <span>Rooms Assigned to Floors</span>
          </div>
          <div className="checklist-item">
            <span className="check-icon">○</span>
            <span>SmartThings Integrated</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelOverview;
